'use client'

import type { UploadedUserFile } from './uploaded-user-file'

/**
 * Client half of the "we already have these bytes" shortcut.
 *
 * Hash the selected files, ask the server in deterministic chunks, and on a hit
 * skip staging entirely — no upload, no Sharp decode, no Blob write.
 *
 * ─── THE BATCHER IS THE POINT ───────────────────────────────────────
 *
 * A timer-based batcher is subtly wrong here: hashing is deliberately limited
 * to two files at a time, so slow devices finish hashes in pairs and flush a
 * request for every pair. The public batch API owns the complete selection and
 * chunks it by the route's fixed cap, making a 500-file selection eight
 * requests regardless of device speed.
 */

/** Must not exceed the route's own per-request cap. */
const MAX_BATCH = 64

/**
 * A hung lookup must never hold up the upload.
 *
 * Nothing in the client upload path used to carry a deadline, and `fetch` has
 * no default timeout: a request that black-holes — a mobile network handover, a
 * captive portal, a proxy that accepts the connection and never answers — hangs
 * until the tab is closed. Here that would stall every file waiting on the
 * batch. The shortcut is optional by construction, so it gets a short one.
 */
const LOOKUP_TIMEOUT_MS = 8000

/**
 * Files hashed at once.
 *
 * `crypto.subtle.digest` has no streaming API, so hashing materializes the
 * WHOLE file as an ArrayBuffer. The staging pool can run 16 wide, and 16
 * simultaneous 8MB buffers on top of the uploads themselves is an
 * out-of-memory crash on a phone. Hashing is fast and CPU-bound, so serializing
 * it costs almost nothing in wall-clock while bounding peak memory to
 * MAX_HASH_BYTES × this.
 */
const MAX_CONCURRENT_HASHES = 2

/**
 * Files above this size are not hashed.
 *
 * Hashing reads the whole file into memory on top of the read the upload
 * already does. Below the direct-normalize ceiling that second read is
 * negligible next to a round trip; for a 25MB original it is not, and the
 * multipart staged path those take is already resumable per part. Skipping
 * them keeps the shortcut a pure win rather than a trade.
 */
const MAX_HASH_BYTES = 8 * 1024 * 1024

async function lookupHashes(
  hashes: ReadonlyArray<string>
): Promise<Record<string, UploadedUserFile>> {
  try {
    const response = await fetch('/api/upload/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ hashes }),
      signal: AbortSignal.timeout(LOOKUP_TIMEOUT_MS),
    })
    if (!response.ok) {
      return {}
    }
    const body = (await response.json()) as {
      found?: Record<string, UploadedUserFile>
    }
    return body.found && typeof body.found === 'object' ? body.found : {}
  } catch {
    // Any failure is a MISS, never an error: the caller simply stages the file
    // normally. A wrong miss costs one redundant upload; a wrong hit would
    // attach the wrong image to a customer's brand.
    return {}
  }
}

/** Serializes hashing so peak memory is bounded — see MAX_CONCURRENT_HASHES. */
let hashSlots = MAX_CONCURRENT_HASHES
const hashWaiters: Array<() => void> = []

async function acquireHashSlot(): Promise<void> {
  if (hashSlots > 0) {
    hashSlots--
    return
  }
  await new Promise<void>((resolve) => hashWaiters.push(resolve))
}

function releaseHashSlot(): void {
  const next = hashWaiters.shift()
  if (next) {
    next()
    return
  }
  hashSlots++
}

/**
 * SHA-256 of the file's bytes, or null when hashing is unavailable or not worth
 * it. `crypto.subtle` requires a secure context, so this is null over plain
 * HTTP — the caller then just stages normally.
 *
 * The buffer is released before returning: holding it while the caller goes on
 * to upload would double this file's memory for the whole transfer.
 */
export async function hashFileContent(file: File): Promise<string | null> {
  if (file.size > MAX_HASH_BYTES) {
    return null
  }
  const subtle = globalThis.crypto?.subtle
  if (!subtle) {
    return null
  }
  await acquireHashSlot()
  try {
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer())
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return null
  } finally {
    releaseHashSlot()
  }
}

/**
 * The already-normalized image for this file, if the server has seen these
 * exact bytes before. Null means "stage it".
 */
export async function findAlreadyUploaded(
  file: File
): Promise<UploadedUserFile | null> {
  const [hit] = await findAlreadyUploadedBatch([file])
  return hit
}

/**
 * Resolve an entire selection with request count bounded by `ceil(n / 64)`.
 *
 * Hashing remains two-wide to bound memory. Lookup requests are deliberately
 * sequential: at most eight are needed for the 500-file product cap, and this
 * keeps a large selection from creating a burst against Blob and the API rate
 * limiter. The staging caller pipelines the next lookup window with the
 * current upload window, hiding most of this latency.
 */
export async function findAlreadyUploadedBatch(
  files: ReadonlyArray<File>
): Promise<Array<UploadedUserFile | null>> {
  const hashes = await Promise.all(files.map(hashFileContent))
  const found: Record<string, UploadedUserFile> = {}

  // biome-ignore-start lint/performance/noAwaitInLoops: bounded lookup pressure is intentional
  for (let start = 0; start < hashes.length; start += MAX_BATCH) {
    const chunk = Array.from(
      new Set(
        hashes
          .slice(start, start + MAX_BATCH)
          .filter((hash): hash is string => hash !== null)
      )
    )
    if (chunk.length === 0) {
      continue
    }
    Object.assign(found, await lookupHashes(chunk))
  }
  // biome-ignore-end lint/performance/noAwaitInLoops: migrated from eslint suppression

  return hashes.map((hash, index) => {
    const hit = hash ? found[hash] : undefined
    if (
      !hit ||
      typeof hit.url !== 'string' ||
      typeof hit.mediaType !== 'string'
    ) {
      return null
    }
    return {
      url: hit.url,
      mediaType: hit.mediaType,
      // Never surface the first uploader's filename across tenants.
      filename: files[index].name || 'upload',
      ...(typeof hit.width === 'number' ? { width: hit.width } : {}),
      ...(typeof hit.height === 'number' ? { height: hit.height } : {}),
    }
  })
}
